import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls =
  "mt-2 h-11 rounded-none bg-[#0A0A0A] border-border focus:ring-2 focus:ring-primary text-white";
const labelCls =
  "text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground";

export const AccountSettings = ({ title = "Admin credentials" }) => {
  const { user, refresh } = useAuth();
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [mail, setMail] = useState({ current_password: "", new_email: "" });
  const [busy, setBusy] = useState("");

  const changePassword = async (e) => {
    e.preventDefault();
    setBusy("pwd");
    try {
      await api.post("/auth/change-password", pwd);
      setPwd({ current_password: "", new_password: "" });
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setBusy("mail");
    try {
      await api.post("/auth/change-email", mail);
      setMail({ current_password: "", new_email: "" });
      await refresh();
      toast.success("Email updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mt-16 border border-border bg-[#141414] p-6 sm:p-8" data-testid="account-settings">
      <p className="text-xs tracking-[0.25em] uppercase font-bold text-muted-foreground">
        Account
      </p>
      <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter mt-2">
        {title}
      </h2>
      <p className="mt-3 text-sm text-muted-foreground">
        Signed in as <span data-testid="account-current-email" className="text-white font-semibold">{user?.email}</span>
      </p>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <form onSubmit={changePassword} data-testid="change-password-form">
          <p className={labelCls}>Change password</p>
          <div className="mt-4 space-y-4">
            <div>
              <Label className={labelCls}>Current password</Label>
              <Input
                data-testid="pwd-current-input"
                type="password"
                required
                value={pwd.current_password}
                onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <Label className={labelCls}>New password</Label>
              <Input
                data-testid="pwd-new-input"
                type="password"
                required
                minLength={6}
                value={pwd.new_password}
                onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                className={inputCls}
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={busy === "pwd"}
            data-testid="pwd-submit-button"
            className="mt-6 h-11 rounded-none bg-primary hover:bg-[#FF5C53] tracking-[0.15em] uppercase text-xs font-bold px-6"
          >
            {busy === "pwd" ? "Saving" : "Update password"}
          </Button>
        </form>

        <form onSubmit={changeEmail} data-testid="change-email-form">
          <p className={labelCls}>Change email</p>
          <div className="mt-4 space-y-4">
            <div>
              <Label className={labelCls}>Current email</Label>
              <Input
                data-testid="mail-current-email-input"
                value={user?.email || ""}
                readOnly
                disabled
                className={`${inputCls} text-muted-foreground`}
              />
            </div>
            <div>
              <Label className={labelCls}>New email</Label>
              <Input
                data-testid="mail-new-input"
                type="email"
                required
                value={mail.new_email}
                onChange={(e) => setMail({ ...mail, new_email: e.target.value })}
                className={inputCls}
                placeholder="you@yourdomain.com"
              />
            </div>
            <div>
              <Label className={labelCls}>Confirm password</Label>
              <Input
                data-testid="mail-current-password-input"
                type="password"
                required
                value={mail.current_password}
                onChange={(e) => setMail({ ...mail, current_password: e.target.value })}
                className={inputCls}
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={busy === "mail"}
            data-testid="mail-submit-button"
            className="mt-6 h-11 rounded-none bg-primary hover:bg-[#FF5C53] tracking-[0.15em] uppercase text-xs font-bold px-6"
          >
            {busy === "mail" ? "Saving" : "Update email"}
          </Button>
        </form>
      </div>
    </div>
  );
};
